import * as React from "react";
import classNames from "classnames";

type GeneratedListItemProps = {
  id: string;
  ref: React.MutableRefObject<any>;
  className: string;
  style: React.CSSProperties;
  onClick: (event: React.MouseEvent<any, MouseEvent>) => void;
  role: string;
  "aria-label": string;
  "aria-selected": boolean;
};

export interface ListItemProps<T extends unknown> {
  id: string;
  active: boolean;
  label: string;
  onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  value: T;
  children: React.ReactNode;
  listBoxRef: React.MutableRefObject<HTMLUListElement | HTMLDivElement>;
  className?: string;

  render: (listItemProps: GeneratedListItemProps) => JSX.Element;
}

export const ListItem = <T extends unknown>({
  id,
  active,
  label,
  value,
  onClick,
  children,
  listBoxRef,
  className,

  render,
}: ListItemProps<T>): JSX.Element => {
  const ref = React.useRef<any>(null);

  React.useEffect(() => {
    if (!active) {
      return;
    }

    if (ref.current) {
      const parentNode = listBoxRef.current;
      const currentNode = ref.current;

      /* - delta */
      if (currentNode.offsetTop < parentNode.scrollTop) {
        // scrollIntoView with top aligned
        currentNode.scrollIntoView({ block: "nearest" });
        // parentNode.scrollTop = currentNode.offsetTop;
      }

      /* + delta */
      if (
        currentNode.offsetTop + currentNode.clientHeight >
        parentNode.scrollTop + parentNode.clientHeight
      ) {
        // scrollIntoView with bottom aligned
        currentNode.scrollIntoView({ block: "nearest" });
      }
    }
  }, [active, value, listBoxRef]);

  const listItemProps: GeneratedListItemProps = {
    id,
    ref,
    className: classNames("dropdown-item", className, {
      active: active,
    }),
    style: {
      cursor: "pointer",
    },
    onClick,
    role: "option",
    "aria-label": label,
    "aria-selected": active,
  };

  if (render) {
    return render(listItemProps);
  }

  return <li {...listItemProps}>{children}</li>;
};
